import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import { N8N_TEAMS_URL } from '@/api/n8n'
import type { AgentMessage, AgentUnreadCount, ChatAgent } from '@/types/agentChat'

const CHATWOOT_ACCOUNT_ID = 3

async function post(command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  const res = await fetch(N8N_TEAMS_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ command, timestamp: Date.now(), ...extra }),
  })
  if (!res.ok) throw new Error(`Agent chat ${res.status}`)
  const text = await res.text()
  if (!text.trim()) return []
  return JSON.parse(text)
}

function rowToMessage(row: Record<string, unknown>): AgentMessage {
  return {
    id:         Number(row.id),
    senderId:   Number(row.sender_id),
    receiverId: Number(row.receiver_id),
    body:       String(row.body ?? ''),
    createdAt:  Math.floor(new Date(String(row.created_at)).getTime() / 1000),
    readAt:     row.read_at ? Math.floor(new Date(String(row.read_at)).getTime() / 1000) : null,
  }
}

// Lista de agentes pra mensagem direta — mesma fonte que a aba Agentes/Dashboard
// usa (API de agentes do Chatwoot), com o status sobreposto pela nossa tabela
// agent_status (mesmo motivo do useAvailability.ts: availability_status do
// Chatwoot fica travado em offline pra quem só acessa via esta dashboard).
export async function fetchChatAgents(token: string, excludeUserId: number): Promise<ChatAgent[]> {
  const [agentsRes, statusRows] = await Promise.all([
    fetch(`${CHATWOOT_BASE}/api/v1/accounts/${CHATWOOT_ACCOUNT_ID}/agents`, {
      headers: authHeaders(token),
    }),
    post('list_agent_status') as Promise<Record<string, unknown>[]>,
  ])
  if (!agentsRes.ok) throw new Error(`Agents ${agentsRes.status}`)
  const agentList = await agentsRes.json().catch(() => [])
  if (!Array.isArray(agentList)) return []

  const statusByUser = new Map(
    (Array.isArray(statusRows) ? statusRows : []).map(r => [Number(r.user_id), String(r.availability)])
  )

  // Online primeiro, depois ocupado, depois offline — dentro de cada grupo,
  // ordem alfabética (mesmo critério de agrupamento de AgentsTab.vue).
  const statusOrder: Record<string, number> = { online: 0, busy: 1, offline: 2 }

  return agentList
    .filter((a: any) => Number(a.id) !== excludeUserId)
    .map((a: any): ChatAgent => ({
      id:           Number(a.id),
      name:         a.name || a.available_name || '—',
      email:        a.email || '',
      avatarUrl:    a.thumbnail || a.avatar_url || '',
      availability: (statusByUser.get(Number(a.id)) ?? a.availability_status ?? 'offline') as ChatAgent['availability'],
    }))
    .sort((a, b) => {
      const byStatus = (statusOrder[a.availability] ?? 2) - (statusOrder[b.availability] ?? 2)
      return byStatus !== 0 ? byStatus : a.name.localeCompare(b.name, 'pt-BR')
    })
}

export async function sendAgentMessage(senderId: number, receiverId: number, body: string): Promise<void> {
  await post('send_agent_message', { sender_id: senderId, receiver_id: receiverId, body })
}

// Sem sinceId: carga inicial da thread (últimas 200). Com sinceId: só o que
// chegou depois — usado pelo polling pra não reler a thread inteira a cada ciclo.
export async function fetchAgentMessages(userId: number, otherId: number, sinceId?: number): Promise<AgentMessage[]> {
  const data = await post('get_agent_messages', { user_id: userId, other_id: otherId, since_id: sinceId ?? null })
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  const messages = rows.map(rowToMessage)
  // Carga inicial vem DESC (mais recente primeiro) pra permitir LIMIT 200 no fim
  // da conversa; o front sempre exibe em ordem cronológica. `sinceId` pode ser
  // 0 legitimamente (falsy, mas "fornecido") — checar contra null/undefined,
  // não truthiness.
  return sinceId != null ? messages : messages.reverse()
}

export async function markAgentMessagesRead(userId: number, otherId: number): Promise<void> {
  await post('mark_agent_messages_read', { user_id: userId, other_id: otherId })
}

export async function fetchAgentUnreadCounts(userId: number): Promise<AgentUnreadCount[]> {
  const data = await post('list_agent_unread_counts', { user_id: userId })
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  return rows.map(r => ({ senderId: Number(r.sender_id), unread: Number(r.unread) }))
}
